import React, { useState, useEffect, useRef } from 'react';
import {
  ChevronRight,
  ChevronDown,
  Menu,
  X,
  Play,
  Dumbbell,
  Zap,
  Heart,
  Target,
  Trophy,
  Users,
  Star,
  CheckCircle,
  Smartphone,
  Shield,
} from 'lucide-react';
import ReactCountryFlag from 'react-country-flag';

const LANGUAGES = [
  { code: 'en', name: 'English', flag: 'GB' },
  { code: 'sl', name: 'Slovenščina', flag: 'SI' },
];

const content = {
  en: {
    nav: {
      programs: "Programs",
      howItWorks: "How It Works",
      benefits: "Benefits",
      faq: "FAQ",
    },
    hero: {
      badge: "Your Digital Fitness Solution",
      title: "Train Smarter. Live Better.",
      titleHighlight: "Reach Your Goals.",
      subtitle: "Get access to 100+ expert workout videos, nutrition plans, yoga & meditation — delivered straight to your mobile. Train anytime, anywhere.",
      cta: "Start Training Now",
      ctaSub: "€4.98/week · Cancel anytime",
      trustLine: "Join thousands of active members",
      price: "€2.49/SMS · 2 SMS per week",
    },
    stats: [
      { value: "100+", label: "Workout Videos" },
      { value: "50+", label: "Nutrition Plans" },
      { value: "24/7", label: "Access" },
      { value: "5★", label: "Rated Content" },
    ],
    programs: {
      title: "Training Programs for Every Goal",
      subtitle: "From fat loss to muscle gain — we have the right plan for you",
      items: [
        { icon: "weight", title: "Bodyweight Training", desc: "No gym needed. Effective full-body workouts you can do anywhere.", badge: "Popular" },
        { icon: "muscle", title: "Muscle Building", desc: "Progressive strength programs designed by certified fitness coaches.", badge: "Intense" },
        { icon: "fitness", title: "Cardio & HIIT", desc: "High-energy sessions that torch calories and boost endurance.", badge: "Fat Burn" },
        { icon: "yoga", title: "Yoga & Flexibility", desc: "Improve mobility, reduce stress, and build body awareness.", badge: "Relax" },
        { icon: "meditation", title: "Meditation", desc: "Mental fitness is just as important. Clear your mind, boost focus.", badge: "Mindful" },
        { icon: "nutrition", title: "Nutrition Plans", desc: "Eat right to fuel your performance with expert meal guides.", badge: "Fuel Up" },
      ],
    },
    howItWorks: {
      title: "Get Started in Minutes",
      subtitle: "Simple, fast, and works on any phone",
      steps: [
        { title: "Enter Your Number", description: "Enter your Slovenian mobile number to subscribe to Loovafit." },
        { title: "Receive SMS Access", description: "You'll get an SMS with a link to your personal fitness portal." },
        { title: "Train & Transform", description: "Access all workout videos, nutrition plans and more — start immediately." },
      ],
    },
    benefits: {
      title: "Why Choose Loovafit?",
      subtitle: "Everything you need to crush your fitness goals",
      items: [
        { title: "Expert-Led Workouts", description: "All content created by certified fitness professionals and nutritionists." },
        { title: "Works on Any Device", description: "Access your portal via SMS link — no app download required." },
        { title: "New Content Weekly", description: "Fresh workout and nutrition content added regularly so you never plateau." },
        { title: "Cancel Anytime", description: "No long-term contracts. Send STOP LOOV to 3443 to unsubscribe instantly." },
      ],
    },
    faq: {
      title: "Frequently Asked Questions",
      items: [
        { q: "Can I download or watch as much content as I want?", a: "Yes! Once subscribed, you can watch or download any content available on the portal. Access it via the SMS link you receive after subscribing." },
        { q: "Where do I find the portal?", a: "After subscribing, you will receive an SMS with all the details including a link to your personal fitness portal." },
        { q: "Is this a free service?", a: "No, this is a subscription service priced at €2.49/SMS, with 2 SMS per week (incl. VAT). You can unsubscribe at any time." },
        { q: "How do I contact you?", a: "You can always reach us by email at loovafit.si@silverlines.info or call +386 16001850 (Mon–Fri, 9–17h)." },
        { q: "How do I cancel my subscription?", a: "Send STOP LOOV to 3443. You'll receive a confirmation SMS once unsubscribed." },
        { q: "Who provides this service?", a: "Loovafit is provided by GoMobee LTD, Glashaus (Level 3) 263, Triq ix-Xatt, Gzira GZR 1020, Malta. Billing partner: NTH AG, Hardturmstrasse 161, 8005 Zurich, Switzerland." },
      ],
    },
    cta: {
      title: "Ready to Transform Your Body?",
      subtitle: "Join thousands of people already training with Loovafit. Get instant access to expert fitness content.",
      button: "Get Access Now",
    },
    footer: {
      privacy: "Privacy Policy",
      terms: "Terms & Conditions",
      contact: "Contact",
      faq: "FAQ",
      disclaimer: "For Telekom Slovenije, Telemach and T-2 users: enter the activation code from the SMS to confirm your subscription with DA. For A1 users: confirm with DA to 34439090. LOOVAFIT is your digital fitness solution. Service price: €4.98 incl. VAT per week (2 SMS/week at €2.49 incl. VAT each). To unsubscribe, send STOP LOOV to 3443. Service is charged to your mobile bill or prepaid balance. You must be the account holder or have explicit permission. Provider: GoMobee LTD, Glashaus (Level 3) 263, Triq ix-Xatt, Gzira GZR 1020, Malta. Billing: NTH AG, Hardturmstrasse 161, 8005 Zurich, Switzerland. Email: loovafit.si@silverlines.info · Phone: +386 16001850 (9–17h)",
    },
  },
  sl: {
    nav: {
      programs: "Programi",
      howItWorks: "Kako deluje",
      benefits: "Prednosti",
      faq: "FAQ",
    },
    hero: {
      badge: "Vaša digitalna fitnes rešitev",
      title: "Trenirajte pametneje. Živite bolje.",
      titleHighlight: "Dosezite svoje cilje.",
      subtitle: "Pridobite dostop do 100+ strokovnih vadbenih videov, prehranskih načrtov, joge in meditacije — dostavljenih neposredno na vaš mobilni telefon. Trenirajte kadarkoli in kjerkoli.",
      cta: "Začnite trenirati",
      ctaSub: "€4,98/teden · Odjava kadarkoli",
      trustLine: "Pridružite se tisočim aktivnim članom",
      price: "€2,49/SMS · 2 SMS na teden",
    },
    stats: [
      { value: "100+", label: "Vadbenih videov" },
      { value: "50+", label: "Prehranskih načrtov" },
      { value: "24/7", label: "Dostop" },
      { value: "5★", label: "Ocenjena vsebina" },
    ],
    programs: {
      title: "Vadbeni programi za vsak cilj",
      subtitle: "Od hujšanja do krepitve mišic — imamo pravi načrt za vas",
      items: [
        { icon: "weight", title: "Vadba z lastno težo", desc: "Brez telovadnice. Učinkovite celotelesne vadbe, ki jih izvajate kjerkoli.", badge: "Priljubljeno" },
        { icon: "muscle", title: "Krepitev mišic", desc: "Progresivni programi moči, ki so jih zasnovali certificirani fitnes trenerji.", badge: "Intenzivno" },
        { icon: "fitness", title: "Kardio in HIIT", desc: "Visoko energetske vadbe, ki porabijo kalorije in povečajo vzdržljivost.", badge: "Sežig maščob" },
        { icon: "yoga", title: "Joga in raztezanje", desc: "Izboljšajte gibljivost, zmanjšajte stres in razvijte telesno ozaveščenost.", badge: "Sprostitev" },
        { icon: "meditation", title: "Meditacija", desc: "Mentalna kondicija je enako pomembna. Sprostite misli, povečajte fokus.", badge: "Mindful" },
        { icon: "nutrition", title: "Prehranski načrti", desc: "Jejte prav, da nakrmite svojo zmogljivost s strokovnimi jedilniki.", badge: "Gorivo" },
      ],
    },
    howItWorks: {
      title: "Začnite v minutah",
      subtitle: "Preprosto, hitro in deluje na kateremkoli telefonu",
      steps: [
        { title: "Vnesite svojo številko", description: "Vnesite svojo slovensko mobilno številko za naročnino na Loovafit." },
        { title: "Prejmite SMS dostop", description: "Prejmete SMS s povezavo do vašega osebnega fitnes portala." },
        { title: "Trenirajte in se preobrazite", description: "Dostopajte do vseh vadbenih videov, prehranskih načrtov in več — takoj začnite." },
      ],
    },
    benefits: {
      title: "Zakaj izbrati Loovafit?",
      subtitle: "Vse, kar potrebujete za dosego vaših fitnes ciljev",
      items: [
        { title: "Vadbe strokovnjakov", description: "Vsa vsebina je ustvarjena s strani certificiranih fitnes strokovnjakov in nutricionistov." },
        { title: "Deluje na vsaki napravi", description: "Dostopajte do portala prek SMS povezave — prenos aplikacije ni potreben." },
        { title: "Nova vsebina vsak teden", description: "Redno dodajamo svežo vadbeno in prehransko vsebino, da ne stagnate." },
        { title: "Odjava kadarkoli", description: "Brez dolgoročnih pogodb. Pošljite STOP LOOV na 3443 za takojšnjo odjavo." },
      ],
    },
    faq: {
      title: "Pogosta vprašanja",
      items: [
        { q: "Ali lahko prenesem ali si ogledam poljubno število vsebin?", a: "Da! Ko se naročite, si lahko ogledate ali prenesete katero koli vsebino na portalu. Dostopajte prek SMS povezave, ki jo prejmete po naročitvi." },
        { q: "Kje lahko najdem portal?", a: "Po naročilu boste prejeli SMS z vsemi podrobnostmi, vključno s povezavo do vašega osebnega fitnes portala." },
        { q: "Je to brezplačna storitev?", a: "Ne, to je naročniška storitev s ceno 2,49 €/SMS, 2 SMS na teden z vključenim DDV. Odjavite se lahko kadarkoli." },
        { q: "Kako vas lahko kontaktiram?", a: "Pišite nam na loovafit.si@silverlines.info ali pokličite +386 16001850 (pon–pet, 9–17h)." },
        { q: "Kako se odjavim od naročnine?", a: "Pošljite STOP LOOV na 3443. Prejmete SMS potrditev odjave." },
        { q: "Kdo zagotavlja to storitev?", a: "Loovafit zagotavlja GoMobee LTD, Glashaus (Level 3) 263, Triq ix-Xatt, Gzira GZR 1020, Malta. Partner za zaračunavanje: NTH AG, Hardturmstrasse 161, 8005 Zürich, Švica." },
      ],
    },
    cta: {
      title: "Pripravljeni na preobrazbo?",
      subtitle: "Pridružite se tisočim ljudi, ki že trenirajo z Loovafit. Pridobite takojšnji dostop do strokovne fitnes vsebine.",
      button: "Pridobi dostop",
    },
    footer: {
      privacy: "Pravilnik o zasebnosti",
      terms: "Pogoji poslovanja",
      contact: "Kontakt",
      faq: "Pogosta vprašanja",
      disclaimer: "Za uporabnike Telekoma Slovenije, Telemacha in T-2 vnesite aktivacijsko kodo iz SMS sporočila za potrditev naročnine na DA. Za uporabnike A1 potrdite z DA na 34439090. LOOVAFIT je vaša digitalna fitnes rešitev. Cena storitve je 4,98 € z DDV na teden (2 SMS na teden po 2,49 € z DDV). Od storitve se odjavite s pošiljanjem STOP LOOV na 3443. Storitev bo bremenjena vašemu mobilnemu ali predplačniškemu računu. Za uporabo storitev morate biti lastnik naročnine ali imeti izrecno dovoljenje lastnika. Ponudnik: GoMobee LTD Glashaus (Level 3) 263, Triq ix-Xatt, Gzira GZR 1020, Malta. Zaračunavanje: NTH AG, Hardturmstrasse 161, 8005 Zürich, Švica. E-pošta: loovafit.si@silverlines.info · Tel: +386 16001850 (9–17h)",
    },
  },
};

const programIcons = {
  weight: Dumbbell,
  muscle: Trophy,
  fitness: Zap,
  yoga: Heart,
  meditation: Star,
  nutrition: Target,
};

const programColors = [
  "from-blue-500 to-blue-700",
  "from-orange-500 to-red-600",
  "from-emerald-500 to-teal-600",
  "from-purple-500 to-pink-600",
  "from-indigo-500 to-violet-600",
  "from-amber-500 to-orange-600",
];

const programBg = [
  "from-blue-50 to-blue-100",
  "from-orange-50 to-red-50",
  "from-emerald-50 to-teal-50",
  "from-purple-50 to-pink-50",
  "from-indigo-50 to-violet-50",
  "from-amber-50 to-orange-50",
];

function App() {
  const [lang, setLang] = useState('sl');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const dropdownRef = useRef(null);

  const t = content[lang];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setLangDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLang = LANGUAGES.find(l => l.code === lang);

  return (
    <div className="min-h-screen bg-white font-body text-dark">

      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <a href="/" className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center shadow-sm">
                <Dumbbell className="w-5 h-5 text-white" />
              </div>
              <span className="font-heading font-bold text-xl text-dark">
                Loova<span className="text-primary">fit</span>
              </span>
            </a>

            <nav className="hidden md:flex items-center space-x-8">
              <a href="#programs" className="text-dark/70 hover:text-primary transition-colors font-medium text-sm">{t.nav.programs}</a>
              <a href="#how-it-works" className="text-dark/70 hover:text-primary transition-colors font-medium text-sm">{t.nav.howItWorks}</a>
              <a href="#benefits" className="text-dark/70 hover:text-primary transition-colors font-medium text-sm">{t.nav.benefits}</a>
              <a href="#faq" className="text-dark/70 hover:text-primary transition-colors font-medium text-sm">{t.nav.faq}</a>
            </nav>

            <div className="flex items-center space-x-3">
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                  className="flex items-center space-x-2 px-3 py-2 rounded-lg border border-gray-200 hover:border-primary/40 transition-colors text-sm bg-white"
                >
                  <ReactCountryFlag countryCode={currentLang.flag} svg style={{ width: '18px', height: '14px' }} />
                  <span className="font-medium hidden sm:inline">{currentLang.code.toUpperCase()}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted" />
                </button>
                {langDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
                    {LANGUAGES.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => { setLang(l.code); setLangDropdownOpen(false); }}
                        className={`w-full flex items-center space-x-3 px-4 py-2.5 hover:bg-light transition-colors text-sm ${
                          lang === l.code ? 'text-primary font-semibold bg-primary/5' : 'text-dark'
                        }`}
                      >
                        <ReactCountryFlag countryCode={l.flag} svg style={{ width: '20px', height: '15px' }} />
                        <span>{l.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <a href="/s" className="hidden sm:inline-flex items-center bg-primary text-white px-5 py-2 rounded-xl hover:bg-primary-dark transition-colors font-semibold text-sm shadow-sm">
                {t.hero.cta}
              </a>

              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg text-dark hover:bg-gray-100 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-3 space-y-1">
              {[
                { href: "#programs", label: t.nav.programs },
                { href: "#how-it-works", label: t.nav.howItWorks },
                { href: "#benefits", label: t.nav.benefits },
                { href: "#faq", label: t.nav.faq },
              ].map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="block px-3 py-2.5 text-dark hover:text-primary transition-colors font-medium rounded-lg hover:bg-light"
                >
                  {link.label}
                </a>
              ))}
              <a
                href="/s"
                className="block text-center bg-primary text-white px-4 py-3 rounded-xl font-semibold mt-2"
              >
                {t.hero.cta}
              </a>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1600&q=80')] bg-cover bg-center opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-slate-900/30" />

        {/* Animated glow orbs */}
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-secondary/20 rounded-full blur-3xl animate-pulse-slow" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-32 md:py-40">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-primary/20 border border-primary/30 text-blue-300 px-4 py-1.5 rounded-full text-sm font-semibold mb-6 backdrop-blur-sm">
              <Zap className="w-4 h-4" />
              <span>{t.hero.badge}</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-4 font-heading leading-tight">
              {t.hero.title}
              <br />
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                {t.hero.titleHighlight}
              </span>
            </h1>

            <p className="text-lg md:text-xl text-slate-300 mb-10 leading-relaxed max-w-2xl">
              {t.hero.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row items-start gap-4">
              <a
                href="/s"
                className="group inline-flex items-center bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-2xl text-lg font-bold transition-all shadow-2xl shadow-primary/40 hover:shadow-primary/60 hover:scale-105"
              >
                <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                {t.hero.cta}
                <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </a>
              <div className="flex flex-col justify-center">
                <span className="text-white font-semibold text-sm">{t.hero.price}</span>
                <span className="text-slate-400 text-xs mt-0.5">{t.hero.ctaSub}</span>
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-8">
              <div className="flex -space-x-2">
                {['💪', '🏃', '🧘', '🥗'].map((emoji, i) => (
                  <span key={i} className="w-8 h-8 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center text-sm backdrop-blur-sm">{emoji}</span>
                ))}
              </div>
              <span className="text-slate-400 text-sm">{t.hero.trustLine}</span>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-white/5 backdrop-blur-md border-t border-white/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
              {t.stats.map((stat, i) => (
                <div key={i} className={`py-5 px-6 text-center ${i < t.stats.length - 1 ? 'border-r border-white/10' : ''}`}>
                  <div className="text-2xl font-bold text-white font-heading">{stat.value}</div>
                  <div className="text-slate-400 text-xs mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section id="programs" className="py-24 bg-light">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Trophy className="w-4 h-4" />
              <span>{lang === 'en' ? 'Training Library' : 'Knjižnica vsebin'}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.programs.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.programs.subtitle}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.programs.items.map((prog, i) => {
              const Icon = programIcons[prog.icon];
              return (
                <a
                  key={i}
                  href="/s"
                  className={`group relative bg-gradient-to-br ${programBg[i]} rounded-2xl p-6 border border-white hover:border-primary/20 hover:shadow-xl transition-all duration-300 overflow-hidden`}
                >
                  <div className="absolute top-4 right-4">
                    <span className={`inline-block bg-gradient-to-r ${programColors[i]} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                      {prog.badge}
                    </span>
                  </div>
                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${programColors[i]} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-dark text-lg mb-2">{prog.title}</h3>
                  <p className="text-muted text-sm leading-relaxed mb-4">{prog.desc}</p>
                  <span className="inline-flex items-center text-sm font-semibold text-primary group-hover:translate-x-1 transition-transform">
                    {lang === 'en' ? 'Explore' : 'Oglej si'} <ChevronRight className="w-4 h-4 ml-1" />
                  </span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-secondary" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Smartphone className="w-4 h-4" />
              <span>{lang === 'en' ? 'Simple Setup' : 'Enostavna nastavitev'}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.howItWorks.title}</h2>
            <p className="text-muted text-lg max-w-2xl mx-auto">{t.howItWorks.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-20 left-[calc(33%-20px)] right-[calc(33%-20px)] h-0.5 bg-gradient-to-r from-primary/30 via-primary to-primary/30" />
            {t.howItWorks.steps.map((step, i) => (
              <div key={i} className="text-center relative group">
                <div className="relative inline-flex items-center justify-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-primary to-primary-dark rounded-2xl shadow-xl shadow-primary/30 flex items-center justify-center relative z-10 group-hover:scale-110 transition-transform">
                    <span className="text-white font-heading font-bold text-3xl">{i + 1}</span>
                  </div>
                  <div className="absolute inset-0 bg-primary/20 rounded-2xl blur-md" />
                </div>
                <h3 className="text-xl font-bold text-dark mb-3 font-heading">{step.title}</h3>
                <p className="text-muted leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-14">
            <a
              href="/s"
              className="inline-flex items-center bg-primary text-white px-8 py-4 rounded-2xl text-lg font-bold hover:bg-primary-dark transition-all shadow-xl shadow-primary/30 hover:shadow-primary/50 hover:scale-105"
            >
              {t.cta.button}
              <ChevronRight className="ml-2 w-5 h-5" />
            </a>
          </div>
        </div>
      </section>

      {/* Visual Feature Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1600&q=80')] bg-cover bg-center opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/70 to-slate-900/40" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div id="benefits" className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center space-x-2 bg-white/10 border border-white/20 text-blue-300 px-4 py-1.5 rounded-full text-sm font-semibold mb-6">
                <Shield className="w-4 h-4" />
                <span>{lang === 'en' ? 'Why Loovafit' : 'Zakaj Loovafit'}</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 font-heading">{t.benefits.title}</h2>
              <p className="text-slate-300 text-lg mb-10">{t.benefits.subtitle}</p>
              <div className="space-y-6">
                {t.benefits.items.map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="w-10 h-10 bg-primary/20 border border-primary/30 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-1 font-heading">{item.title}</h3>
                      <p className="text-slate-400 text-sm leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-10">
                <a
                  href="/s"
                  className="inline-flex items-center bg-primary text-white px-7 py-3.5 rounded-xl font-bold hover:bg-primary-dark transition-all shadow-lg shadow-primary/40 hover:scale-105"
                >
                  {t.cta.button}
                  <ChevronRight className="ml-2 w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Feature cards */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: Dumbbell, label: lang === 'en' ? 'Strength' : 'Moč', color: 'from-blue-500 to-blue-700', count: '40+' },
                { icon: Heart, label: lang === 'en' ? 'Cardio' : 'Kardio', color: 'from-red-500 to-rose-600', count: '30+' },
                { icon: Star, label: 'Yoga', color: 'from-purple-500 to-violet-600', count: '20+' },
                { icon: Target, label: lang === 'en' ? 'Nutrition' : 'Prehrana', color: 'from-amber-500 to-orange-600', count: '50+' },
              ].map((card, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors backdrop-blur-sm">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4 shadow-lg`}>
                    <card.icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-white font-heading">{card.count}</div>
                  <div className="text-slate-400 text-sm">{card.label}</div>
                </div>
              ))}
              <div className="col-span-2 bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex items-center space-x-3 mb-2">
                  <Users className="w-6 h-6 text-blue-400" />
                  <span className="text-white font-bold font-heading text-xl">{lang === 'en' ? 'Community' : 'Skupnost'}</span>
                </div>
                <p className="text-slate-400 text-sm">{lang === 'en' ? 'Join thousands of Slovenian athletes already training with Loovafit.' : 'Pridružite se tisočim slovenskim športnikom, ki že trenirajo z Loovafit.'}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-light">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-secondary/10 text-secondary-dark px-4 py-1.5 rounded-full text-sm font-semibold mb-4">
              <Star className="w-4 h-4" />
              <span>{lang === 'en' ? 'Support' : 'Podpora'}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4 font-heading">{t.faq.title}</h2>
          </div>
          <div className="space-y-3">
            {t.faq.items.map((item, i) => (
              <div key={i} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-dark pr-4 leading-snug">{item.q}</span>
                  <ChevronDown className={`w-5 h-5 text-muted flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-50">
                    <p className="text-muted leading-relaxed pt-4">{item.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-gradient-to-br from-primary to-primary-dark relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=1600&q=80')] bg-cover bg-center opacity-10" />
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border-2 border-white rounded-full" />
          <div className="absolute bottom-10 right-10 w-60 h-60 border-2 border-white rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 border border-white rounded-full" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Trophy className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading">{t.cta.title}</h2>
          <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">{t.cta.subtitle}</p>
          <a
            href="/s"
            className="inline-flex items-center bg-white text-primary px-8 py-4 rounded-2xl text-lg font-bold hover:bg-gray-50 transition-all shadow-2xl hover:scale-105"
          >
            <Play className="w-5 h-5 mr-2" />
            {t.cta.button}
            <ChevronRight className="ml-2 w-5 h-5" />
          </a>
          <p className="text-white/60 text-sm mt-4">{t.hero.price}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="flex justify-center items-center space-x-2 mb-6">
              <div className="w-8 h-8 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center">
                <Dumbbell className="w-4 h-4 text-white" />
              </div>
              <span className="font-heading font-bold text-lg">
                Loova<span className="text-blue-400">fit</span>
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-6 text-sm">
              <a href="/privacy.html" className="text-slate-400 hover:text-white transition-colors">{t.footer.privacy}</a>
              <span className="text-slate-700 hidden sm:inline">|</span>
              <a href="/terms.html" className="text-slate-400 hover:text-white transition-colors">{t.footer.terms}</a>
              <span className="text-slate-700 hidden sm:inline">|</span>
              <a href="#faq" className="text-slate-400 hover:text-white transition-colors">{t.footer.faq}</a>
              <span className="text-slate-700 hidden sm:inline">|</span>
              <a href="/contact.html" className="text-slate-400 hover:text-white transition-colors">{t.footer.contact}</a>
            </div>
            <p className="text-slate-600 text-sm mb-8">
              © {new Date().getFullYear()} Loovafit – GoMobee LTD. {lang === 'en' ? 'All rights reserved.' : 'Vse pravice pridržane.'}
            </p>
            <div className="border-t border-slate-800 pt-8 mt-4">
              <div className="max-w-4xl mx-auto">
                <p className="text-xs text-slate-600 leading-relaxed">
                  {t.footer.disclaimer}
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
